/* eslint-disable react/jsx-wrap-multilines */
import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import { Button, Dropdown, Icon, Image, Popup } from 'semantic-ui-react';

import SplitButton from './SplitButton';

import '../css/GradebookHeader.less';

import assessmentDefaultCardImage from '../img/default-assessment.svg';
import iconInfoBlack from '../img/icon-info.svg';
import iconInfoWhite from '../img/icon-info-white.svg';
import iconObservationalPencil from '../img/icon-observational-pencil.svg';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { GRADEBOOK_CONTEXT } from '../constants';

import Auth from '../managers/AuthManager';
import { CONTENT_ITEM_TYPES } from '../managers/ContentManager';

import { generateRobustUrl, getStyleVar } from '../utils';

import AssignmentService from '../services/AssignmentService';
import EngagementService from '../services/EngagementService';
import GradebookMainService from '../services/gradebook/GradebookMainService';
import ImageService from '../services/ImageService';
import PopupService from '../services/PopupService';
import UtilityService from '../services/UtilityService';
import iconAiScored from '../img/icon-ai-scored.svg';
import iconAiOverridden from '../img/icon-ai-overriden.svg';
import iconAiPending from '../img/icon-ai-pending.svg';

import { customAssessmentDefaultCardImageUri } from '../services/UriService';

const GradebookHeader = observer((props) => {
  const {
    // TODO remove, unused // onClickAggregateGradebookButton,
    assignment,
    assignmentEntityTypeId,
    assignable,
    isZeroState,
    licenseExpired,
    isVisible,
    onClickAssignButton,
    onClickEditBulkAssignmentButton,
    onClickExportGradebook,
    onClickGotoCourse,
    onClickPresentAssignment,
    onClickViewContent,
    onClickViewPdf,
    pdfDeliveryFormat,
    teacherMode,
    onClickGradebookContextDetailsButton,
    onClickGradebookContextEngagementButton,
    onClickGradebookContextLikertButton,
    onClickGradebookContextPlacementButton,
    onClickGradebookContextStandardsButton,
    onClickGradebookContextSummaryButton,
    onClickGradebookContextTypingButton,
    onClickInfoIconButton,
    onClickPlacementAddToClassroomButton,
    onClickPlacementAddToGroupButton,
    onClickRecordStudentResponse,
    t
  } = props;

  const {
    assignmentManager, classroomManager, courseManager,
    dialogManager, engagementManager, gradebookManager,
    userManager
  } = useContext(MobXProviderContext);
  const { activeGradebookTable, activeGradebookType, useGradebookHeaderResourceInfo } = gradebookManager;

  const ColorKeyCard = SatCoreComponent('ColorKeyCard');
  const EngagementButtonBar = SatCoreComponent('EngagementButtonBar');
  const GradebookKeyViewMoreModalContent = SatCoreComponent('GradebookKeyViewMoreModalContent');

  const isReadOnly = GradebookMainService.isGradebookReadOnly();

  const showInfoIconButton = isZeroState
    ? !!onClickInfoIconButton
    : AssignmentService.showAssignmentInfoIconButton(assignment, false);

  const entityTypeId = assignmentEntityTypeId || assignment?.contentItemEntityTypeId || assignment?.entityTypeId || null;

  const isAssessment = entityTypeId === 'assessment';
  const isLesson = entityTypeId === 'lesson';
  const isLearnosity = entityTypeId?.includes?.('learnosity');
  const isFileResource = entityTypeId === 'file' || entityTypeId === 'file_resource';
  const fileDownloadUrl = isFileResource && assignment?.contentItemId
    ? CONTENT_ITEM_TYPES.getFileDownloadUrl(assignment.contentItemId)
    : null;

  const handleClickGradebookContextButton = (event) => {
    const { value } = event.target.dataset;
    // clear selections when changing views.
    gradebookManager.clearBulkCheckedAssignmentMembers();
    switch (value) {
      // TODO remove, unused
      // case 'aggregate':
      //   gradebookManager.setActiveGradebookType('aggregate');
      //   return onClickAggregateGradebookButton();
      case GRADEBOOK_CONTEXT.SUMMARY:
        gradebookManager.setActiveGradebookDetailsHorizontalScoresPage(1);
        gradebookManager.setActiveGradebookTable(GRADEBOOK_CONTEXT.SUMMARY);
        return onClickGradebookContextSummaryButton();
      case GRADEBOOK_CONTEXT.DETAILS:
        gradebookManager.setActiveGradebookTable(GRADEBOOK_CONTEXT.DETAILS);
        return onClickGradebookContextDetailsButton();
      case GRADEBOOK_CONTEXT.LIKERT:
        gradebookManager.setActiveGradebookTable(GRADEBOOK_CONTEXT.LIKERT);
        return onClickGradebookContextLikertButton();
      case GRADEBOOK_CONTEXT.TYPING:
        gradebookManager.setActiveGradebookTable(GRADEBOOK_CONTEXT.TYPING);
        return onClickGradebookContextTypingButton();
      case GRADEBOOK_CONTEXT.ENGAGEMENT:
        gradebookManager.setActiveGradebookTable(GRADEBOOK_CONTEXT.ENGAGEMENT);
        engagementManager.clearEngagementTable();
        engagementManager.setActiveEngagementTable('summary');
        return onClickGradebookContextEngagementButton();
      case GRADEBOOK_CONTEXT.STANDARDS:
        gradebookManager.setActiveGradebookTable(GRADEBOOK_CONTEXT.STANDARDS);
        return onClickGradebookContextStandardsButton();
      case GRADEBOOK_CONTEXT.PLACEMENT:
        gradebookManager.setActiveGradebookTable(GRADEBOOK_CONTEXT.PLACEMENT);
        return onClickGradebookContextPlacementButton?.();
    }
  };

  const handleClickAssignmentButton = (assignment) => {
    onClickAssignButton(assignment, true); // true for reassign
  };

  const renderAssignmentNameAndDescription = () => {
    const aiGradable = userManager.user.institutionAiEnabled && userManager.user.publisherAiEnabled;

    const assignmentName = assignment.resourceWebTitle || assignment.name;
    const assignmentDescription = assignment.resourceWebSubtitle;

    const iconInfo = dialogManager.useWhiteInfoIcon ? iconInfoWhite : iconInfoBlack;
    const assignmentNameObj = UtilityService.reactHtmlParserWrapper(assignmentName);
    const assignmentDescriptionObj = UtilityService.reactHtmlParserWrapper(assignmentDescription);
    const contentImageUrl = isZeroState ? null : ImageService.getImageUrl(assignment);

    const assignmentClassroomName = assignment?.classroomName ? (
      t('assignmentClassroomName', { classroomName: assignment.classroomName })
    ) : undefined;

    // Check if the satellite uses the full resource header or just the title/subtitle
    if (useGradebookHeaderResourceInfo) {
      return (
        <div className='assignment-banner'>
          {(contentImageUrl !== null &&
            contentImageUrl !== undefined && contentImageUrl !== '')
            ? <Image alt='' src={contentImageUrl} /> : getDefaultImage()}
          <div className='title-wrapper'>
            <div
              className='flex-header'
              title={assignmentNameObj.stripped}>
              {assignmentNameObj.parsed}
              {showInfoIconButton && (
                <Image
                  alt=''
                  id='info-icon'
                  onClick={onClickInfoIconButton}
                  src={iconInfo} />
              )}
            </div>
            {!!assignmentDescription && (
              <div
                className='flex-subheader flex-subheader-subtitle'
                title={assignmentDescriptionObj.stripped}>
                {assignmentDescriptionObj.parsed}
              </div>
            )}
            {!!assignmentClassroomName && (
              <div
                className='flex-subheader flex-subheader-classroom-name'
                title={assignmentClassroomName}>
                {assignmentClassroomName}
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <>
          <div className='header-h1-wrapper'>
            {PopupService.renderPopup({
              content: assignmentNameObj.stripped,
              trigger: (
                <h1 className='header theme-header-title'>
                  {assignmentNameObj.parsed}
                </h1>
              )
            })}
            {showInfoIconButton && (
              <Image
                alt=''
                onClick={onClickInfoIconButton}
                src={iconInfo} />
            )}
          </div>
          {!!assignmentDescription && (
            <div className='header-h3-wrapper'>
              {PopupService.renderPopup({
                content: assignmentDescriptionObj.stripped,
                trigger: (
                  <h3 className='header gradebook-header-subtitle'>
                    {assignmentDescriptionObj.parsed}
                  </h3>
                )
              })}
            </div>
          )}
          {!!assignmentClassroomName && (
            <div className='header-h3-wrapper'>
              {PopupService.renderPopup({
                content: assignmentClassroomName,
                trigger: (
                  <h3 className='header gradebook-header-classroom-name'>
                    {assignmentClassroomName}
                  </h3>
                )
              })}
            </div>
          )}
          {aiGradable && (
            <div className='header-h3-wrapper'>
              <h3 className='header gradebook-header-ai-gradable'>
                {t('aiGradable')}
              </h3>
            </div>
          )}
        </>
      );
    }
  };

  const getDefaultImage = () => {
    const isCustomCourse = courseManager.isCustomCourse(courseManager.currentCourseId);

    let customLeafCardImageClassNames = 'default';
    let defaultCustomLeafCardImg = assessmentDefaultCardImage;

    if (courseManager.useSpecialCustomCourseCardImages && isCustomCourse) {
      customLeafCardImageClassNames += 'custom-default-special';
      defaultCustomLeafCardImg = customAssessmentDefaultCardImageUri;
    }
    return (
      <Image
        alt=''
        className={customLeafCardImageClassNames}
        src={defaultCustomLeafCardImg} />
    );
  };

  const renderGradebookDetailsColorKeyCard = () => {
    const { hasObservationalGradebookData, isGradebookLikert } = gradebookManager;

    const { DETAILS, STANDARDS, LIKERT } = GRADEBOOK_CONTEXT;
    if (activeGradebookTable !== DETAILS && activeGradebookTable !== STANDARDS && activeGradebookTable !== LIKERT) {
      return null;
    }

    const allowAccessibilityTriangle = gradebookManager.allowFacultyGradebookAccessibility;

    let fullCreditLabel, partialCreditLabel, noCreditLabel;

    if (isGradebookLikert) {
      fullCreditLabel = 'likertFullCredit';
      partialCreditLabel = 'likertPartialCredit';
      noCreditLabel = 'likertNoCredit';
    } else {
      fullCreditLabel = 'fullCredit';
      partialCreditLabel = 'partialCredit';
      noCreditLabel = 'noCredit';
    }

    const colorKeyDataArray = gradebookManager.hideGradebookColorKeys ? [] : [
      {
        color: getStyleVar('--gradebook-detail-full-credit'),
        colorSquareClassName: classNames('full-credit', {
          'accessibility-triangle': allowAccessibilityTriangle
        }),
        label: fullCreditLabel
      },
      {
        color: getStyleVar('--gradebook-detail-partial-credit'),
        colorSquareClassName: classNames('partial-credit', {
          'accessibility-triangle': allowAccessibilityTriangle
        }),
        label: partialCreditLabel
      },
      {
        color: getStyleVar('--gradebook-detail-no-credit'),
        colorSquareClassName: 'no-credit',
        label: noCreditLabel
      },
    ];

    const aiIconInfoArray = userManager.user.publisherAiEnabled && userManager.user.institutionAiEnabled ? [
      {
        className: 'ai-pending-icon',
        label: 'aiPending',
        src: iconAiPending,
      },
      {
        className: 'ai-scored-icon',
        label: 'aiScored',
        src: iconAiScored,
      },
      {
        className: 'ai-overridden-icon',
        label: 'aiOverridden',
        src: iconAiOverridden,
      },
    ] : [];

    const iconKeyDataArray = [
      {
        // 'No Student Response'
        className: 'no-student-response',
        icon: <span className='cursor-default link-color'>—</span>,
        label: 'noResponse'
        // TODO remove
        // color: 'transparent',
        // colorSquareClassName: classNames('noResponse', {
        //   'accessibility-triangle': allowAccessibilityTriangle
        // }),
        // icon: <span className='clickable link-color'>—</span>,
      },
      {
        // 'Requires Manual Scoring'
        className: 'requires-manual-scoring',
        icon: <Icon className='cursor-default link-color' name='pencil' />,
        label: 'requiresManualScore'
        // TODO remove
        // color: 'transparent',
        // colorSquareClassName: classNames('requiresManualScore', {
        //   'accessibility-triangle': allowAccessibilityTriangle
        // }),
        // icon: <Icon className='clickable link-color' name='pencil' />,
      }
    ];

    let gradebookColorKeyCardWidth;

    if (hasObservationalGradebookData) {
      iconKeyDataArray.push({
        // 'Observational Scoring'
        className: 'observational-scoring',
        icon: <Image className='icon-observational-pencil' src={iconObservationalPencil} />,
        label: 'observationalScoring'
      });
      gradebookColorKeyCardWidth = getStyleVar('--color-key-card-gradebook-observational-width');
    }

    return (
      <div className={`gradebook-colorkeycard-outer-wrapper ${activeGradebookType} ${activeGradebookTable}`}>
        <ColorKeyCard
          additionalClassNames={classNames('gradebook-colorkeycard', {
            'gradebook-ai-colorkeycard': !!aiIconInfoArray,
            'observational': hasObservationalGradebookData
          })}
          aiIconInfoArray={aiIconInfoArray}
          colorKeyDataArray={colorKeyDataArray}
          iconKeyDataArray={iconKeyDataArray}
          maxWidth={gradebookColorKeyCardWidth}
          showViewMoreButton={gradebookManager.showGradebookKeyViewMoreModal}
          viewMoreModalContent={<GradebookKeyViewMoreModalContent />}
          viewMoreModalTitle='gradebookKeyModalTitle'
          width={gradebookColorKeyCardWidth} />
      </div>
    );
  };

  const renderGradebookContextButtons = () => {
    const {
      activeGradebookTable,
      allowGradebookStandards,
      allowGradebookTypingContext,
      allowLearnosityGradebookDetail,
      allowPlacement,
      defaultGradebookTable,
      placementTags
    } = gradebookManager;

    const { hasConventionalGradebookData, hasLikertGradebookData } = gradebookManager;

    const hasGradebookData = hasConventionalGradebookData || hasLikertGradebookData;

    const showDetailsButton = (hasConventionalGradebookData || defaultGradebookTable === GRADEBOOK_CONTEXT.DETAILS) && (
      (isAssessment || isLesson || (isLearnosity && allowLearnosityGradebookDetail))
    );

    const showLikertButton = hasLikertGradebookData || defaultGradebookTable === GRADEBOOK_CONTEXT.LIKERT;

    let showEngagementButton;

    if (typeof EngagementService.shouldShowGradebookEngagementButton === 'undefined') {
      // default case
      showEngagementButton = hasGradebookData && (
        userManager.hasViewEngagementDataPermission && showDetailsButton
      );
    } else {
      // custom case
      showEngagementButton = hasGradebookData && (
        EngagementService.shouldShowGradebookEngagementButton(entityTypeId)
      );
    }

    const showStandardsButton = hasGradebookData && allowGradebookStandards;

    const showTypingButton = hasGradebookData && allowGradebookTypingContext;

    const showPlacementButton = allowPlacement && placementTags?.some((tag) => tag.selected);

    // TODO remove
    // const allowedGradebookContextKeys = [];
    // if (showDetailsButton) {
    //   allowedGradebookContextKeys.push(GRADEBOOK_CONTEXT.DETAILS);
    // }
    // if (showLikertButton) {
    //   allowedGradebookContextKeys.push(GRADEBOOK_CONTEXT.LIKERT);
    // }
    // if (showTypingButton) {
    //   allowedGradebookContextKeys.push(GRADEBOOK_CONTEXT.TYPING);
    // }
    // if (showEngagementButton && !isLearnosity) {
    //   allowedGradebookContextKeys.push(GRADEBOOK_CONTEXT.ENGAGEMENT);
    // }
    // if (showStandardsButton) {
    //   allowedGradebookContextKeys.push(GRADEBOOK_CONTEXT.STANDARDS);
    // }
    // if (allowedGradebookContextKeys.length) {
    //   allowedGradebookContextKeys.unshift(GRADEBOOK_CONTEXT.SUMMARY);
    // }

    const allowedGradebookContextKeys = gradebookManager.gradebookContextOrderArray.filter((key) => {
      switch (key) {
        case GRADEBOOK_CONTEXT.DETAILS:
          return showDetailsButton;
        case GRADEBOOK_CONTEXT.LIKERT:
          return showLikertButton;
        case GRADEBOOK_CONTEXT.TYPING:
          return showTypingButton;
        case GRADEBOOK_CONTEXT.ENGAGEMENT:
          return showEngagementButton && !isLearnosity;
        case GRADEBOOK_CONTEXT.STANDARDS:
          return showStandardsButton;
        case GRADEBOOK_CONTEXT.PLACEMENT:
          return showPlacementButton;
        default:
          return false;
      }
    });

    // SUMMARY is always first if at least one other context button is allowed,
    // or if allowGradebookContextSummaryOnly is enabled
    if (allowedGradebookContextKeys.length || gradebookManager.allowGradebookContextSummaryOnly) {
      allowedGradebookContextKeys.unshift(GRADEBOOK_CONTEXT.SUMMARY);
    }

    // Hide the tab bar when there is only one available context (e.g. summary-only mode).
    if (allowedGradebookContextKeys.length === 1) {
      return null;
    }

    const lastIndex = allowedGradebookContextKeys.length - 1;

    return (
      <div className='gradebook-header-context-buttons-wrapper btns-summary-and-details-wrapper'>
        {allowedGradebookContextKeys.map((gradebookContextKey, index) => {
          return (
            <Button
              key={gradebookContextKey}
              className={classNames(gradebookContextKey, {
                'bar-btn-left': index === 0,
                'bar-btn-middle': index !== 0 && index !== lastIndex,
                'bar-btn-right': index === lastIndex
              })}
              data-value={gradebookContextKey}
              onClick={handleClickGradebookContextButton}
              primary={activeGradebookTable === gradebookContextKey}
              size='tiny'>
              {t(gradebookContextKey)}
            </Button>
          );
        })}
      </div>
    );
  };

  const renderEngagementButtonBar = () => {
    const showEngagementButtonBar = (
      gradebookManager.activeGradebookTable === 'engagement' && engagementManager.activeEngagementTable !== 'summary'
    );
    return showEngagementButtonBar ? <EngagementButtonBar /> : null;
  };

  const renderGotoCourseButton = () => {
    return (
      <div className={classNames('btn-goto-course-wrapper', {
        'is-read-only': isReadOnly
      })}>
        <Button
          className='primary goto-course-button'
          onClick={onClickGotoCourse}>
          {t('goToCourseLabel')}
        </Button>
      </div>
    );
  };

  const renderViewContentButton = () => {
    if (isFileResource) {
      if (!fileDownloadUrl) return null;
      return (
        <div className='btn-view-content-wrapper'>
          <a download href={fileDownloadUrl}>
            <Button
              className='primary btn-view-content'
              type='button'>
              {t('DownloadCourse', 'Download')}
            </Button>
          </a>
        </div>
      );
    }

    if (!onClickViewContent) return null;
    return (
      <div className='btn-view-content-wrapper'>
        <Button
          className='primary btn-view-content'
          onClick={onClickViewContent}
          type='button'>
          {t('viewContentLabel')}
        </Button>
      </div>
    );
  };

  const renderExportGradebookButton = () => {
    return (
      <div className='btn-export-wrapper'>
        <Button
          basic
          className={`btn-export ${activeGradebookTable}`}
          disabled={isZeroState}
          onClick={onClickExportGradebook}
          primary
          type='button'>
          <Icon name='download' />
          {t('export')}
        </Button>
      </div>
    );
  };

  const renderIWouldLikeToDropdown = () => {
    if (activeGradebookTable !== 'summary' || isReadOnly) return null;

    const { isTeacher } = userManager;
    const isScormResource = entityTypeId?.includes?.('scorm');
    const isPdfResource = entityTypeId === 'pdf' || entityTypeId === 'pdf_resource';

    const actions = [];

    // Assign
    if (isTeacher && assignable !== false) {
      let popupMsg;
      if (licenseExpired) {
        popupMsg = t('expiredLicenseButtonAssignMsg');
      } else if (isVisible === false) {
        popupMsg = t('hiddenMsg');
      }
      actions.push({
        disabled: licenseExpired || isVisible === false,
        key: 'assign',
        label: t('teacherButtonAssign'),
        onClick: isZeroState ? () => onClickAssignButton() : () => handleClickAssignmentButton(assignment),
        popupMsg
      });
    }

    // Record student response — ahead of Present when both are available
    if (isTeacher && (isLesson || isAssessment) && gradebookManager.allowTeacherLaunchAsSpecificStudent
      && !teacherMode && onClickRecordStudentResponse) {
      actions.push({
        key: 'record-student-response',
        label: t('teacherButtonRecordStudentResponse', 'Record student response'),
        onClick: () => onClickRecordStudentResponse(assignment)
      });
    }

    // Present
    if (isTeacher && (isLesson || isAssessment || isScormResource || isPdfResource)
      && assignmentManager.allowPresentationMode && !teacherMode && onClickPresentAssignment) {
      let popupMsg;
      if (assignable !== false) {
        if (licenseExpired) {
          popupMsg = t('expiredLicenseButtonAssignMsg');
        } else if (isVisible === false) {
          popupMsg = t('hiddenMsg');
        }
      }
      actions.push({
        disabled: licenseExpired || isVisible === false,
        key: 'present',
        label: t('teacherButtonPresent'),
        onClick: () => onClickPresentAssignment(assignment),
        popupMsg
      });
    }

    // PDF download — for PDF resources with a rich-viewer (flowpaper/docreader) delivery
    const isPdfAndFlowpaper = pdfDeliveryFormat === 'pdf_and_flowpaper';
    const isDocReaderAndFlowpaper = pdfDeliveryFormat === 'pdf_and_docreader';
    if (isTeacher && isPdfResource && (isPdfAndFlowpaper || isDocReaderAndFlowpaper) && onClickViewPdf) {
      actions.push({
        disabled: licenseExpired,
        key: 'download-pdf',
        label: t('teacherButtonPdf', 'PDF'),
        onClick: onClickViewPdf
      });
    }

    // File-resource downloads render in the View Content button slot.
    if (!actions.length) return null;

    const assignAction = actions.find((a) => a.key === 'assign');
    const otherActions = actions.filter((a) => a.key !== 'assign');

    // Always render as split button when there's an Assign action
    if (assignAction) {
      return (
        <div className='iwlt-wrapper'>
          <span className='iwlt-label'>{t('actionDropdownLabel', 'I would like to:')}</span>
          <SplitButton
            disabled={assignAction.disabled}
            items={otherActions}
            label={assignAction.label}
            onClick={assignAction.onClick}
            popupMsg={assignAction.popupMsg}
            popupWide={!licenseExpired ? 'very' : false} />
        </div>
      );
    }

    // No Assign, multiple other actions: plain dropdown
    return (
      <div className='iwlt-wrapper'>
        <span className='iwlt-label'>{t('actionDropdownLabel', 'I would like to:')}</span>
        <Dropdown
          button
          className='basic primary course-tree-leaf-action-dropdown'
          icon='chevron down'
          text={t('actionDropdownLabel', 'I would like to...')}>
          <Dropdown.Menu>
            {actions.map((action) => (
              action.isDownloadLink ? (
                <Dropdown.Item key={action.key}>
                  <a download href={action.downloadUrl}>{action.label}</a>
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  key={action.key}
                  disabled={action.disabled}
                  onClick={action.disabled ? undefined : action.onClick}
                  text={action.label} />
              )
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  const renderPlacementButtons = () => {
    if (activeGradebookTable !== GRADEBOOK_CONTEXT.PLACEMENT) {
      return null;
    }
    const { assignmentToRepInstanceBulkCheckedMap } = gradebookManager;
    const hasChecked = assignmentToRepInstanceBulkCheckedMap.size > 0;

    const addToClassroomBtn = (
      <span className='btn-placement-add-to-classroom-wrapper'>
        <Button
          className={`btn-placement-add-to-classroom ${activeGradebookTable}`}
          disabled={!hasChecked}
          onClick={onClickPlacementAddToClassroomButton}
          primary
          type='button'>
          {t('placementAddToClassroomButton', 'Add to Classroom')}
        </Button>
      </span>
    );

    const addToGroupBtn = classroomManager.includeGroups ? (
      <span className='btn-placement-add-to-group-wrapper'>
        <Button
          className={`btn-placement-add-to-group ${activeGradebookTable}`}
          disabled={!hasChecked}
          onClick={onClickPlacementAddToGroupButton}
          primary
          type='button'>
          {t('placementAddToGroupButton', 'Add to Group')}
        </Button>
      </span>
    ) : null;

    return (
      <div className={classNames('btn-placement-wrapper', { 'is-read-only': isReadOnly })}>
        {!hasChecked ? (
          <Popup
            content={<div>{t('placementAddToClassroomButtonDisabledMessage', 'Select students to add to a classroom.')}</div>}
            on='hover'
            position='bottom right'
            trigger={addToClassroomBtn} />
        ) : addToClassroomBtn}
        {!hasChecked ? (
          <Popup
            content={<div>{t('placementAddToGroupButtonDisabledMessage', 'Select students to add to a group.')}</div>}
            on='hover'
            position='bottom right'
            trigger={addToGroupBtn} />
        ) : addToGroupBtn}
      </div>
    );
  };

  const renderBulkUpdateButton = () => {
    const { assignmentToRepInstanceBulkCheckedMap } = gradebookManager;

    if (activeGradebookTable === GRADEBOOK_CONTEXT.SUMMARY) {
      const button = (
        <div className={classNames('btn-bulk-wrapper', {
          'is-read-only': isReadOnly
        })}>
          <Button
            className={`btn-bulk ${activeGradebookTable}`}
            disabled={!assignmentToRepInstanceBulkCheckedMap.size > 0}
            onClick={onClickEditBulkAssignmentButton}
            primary
            type='button'>
            {t('bulkUpdateButtonLabel')}
          </Button>
        </div>
      );
      if (!assignmentToRepInstanceBulkCheckedMap.size > 0) {
        return (
          <Popup
            content={(
              <div>{t('bulkUpdateButtonDisabledMessage')}</div>
            )}
            on='hover'
            position='bottom right'
            trigger={button} />
        );
      }
      return button;
    } else {
      return null;
    }
  };

  const renderExternalReportButton = () => {
    const { externalReportButton: externalReportButtonConfig } = gradebookManager;
    if (!externalReportButtonConfig?.url) return null;

    const {
      label,
      params = {},
      url
    } = externalReportButtonConfig;
    
    const queryParams = Object.entries(params).reduce((acc, [paramName, assignmentProperty]) => {
      const value = assignment?.[assignmentProperty];
      if (value != null && value !== '') {
        acc[paramName] = value;
      }
      return acc;
    }, {});

    const authKey = userManager?.authKey || Auth.authKey;
    if (authKey) {
      queryParams.authKey = authKey;
    }

    const buttonLabel = label || t('externalReportButtonLabel');

    const handleClickExternalReportButton = () => {
      window.open(generateRobustUrl(url, queryParams), '_blank', 'noopener');
    };

    return (
      <div className='btn-external-report-wrapper'>
        <Button
          basic
          className={`btn-external-report ${activeGradebookTable}`}
          disabled={isZeroState}
          onClick={handleClickExternalReportButton}
          primary
          type='button'>
          {buttonLabel}
        </Button>
      </div>
    );
  };

  return (
    <div className='gradebook-header-container'>
      <div className={`gradebook-header-left ${activeGradebookType} ${activeGradebookTable}`}>
        {renderAssignmentNameAndDescription()}
        {renderGradebookContextButtons()}
        {renderEngagementButtonBar()}
      </div>
      {/* eslint-disable-next-line max-len */}
      <div className={`gradebook-header-right ${activeGradebookType} ${activeGradebookTable} ${useGradebookHeaderResourceInfo ? 'top-align' : ''}`}>
        {activeGradebookTable !== GRADEBOOK_CONTEXT.PLACEMENT && renderIWouldLikeToDropdown()}
        <div className={classNames('button-wrapper', {
          'is-read-only': isReadOnly
        })}>
          {activeGradebookTable !== GRADEBOOK_CONTEXT.PLACEMENT && renderGotoCourseButton()}
          {activeGradebookTable !== GRADEBOOK_CONTEXT.PLACEMENT && renderViewContentButton()}
          {renderPlacementButtons()}
          {activeGradebookTable !== GRADEBOOK_CONTEXT.PLACEMENT && renderBulkUpdateButton()}
          {activeGradebookTable !== GRADEBOOK_CONTEXT.PLACEMENT && renderExternalReportButton()}
          {activeGradebookTable !== GRADEBOOK_CONTEXT.PLACEMENT && renderExportGradebookButton()}
        </div>
        {renderGradebookDetailsColorKeyCard()}
      </div>
    </div>
  );
});
export default GradebookHeader;

SatCoreRegister('GradebookHeader', GradebookHeader);
